import { useEffect, useRef, useState, useCallback } from 'react';
import type { CharacterResponse, CharacterSummary } from './types';
import { fetchCharacterByKey, fetchCharacters } from './lib/api';
import { Header } from './components/Header';
import { PaperDoll } from './components/PaperDoll';
import { StatsPanel } from './components/StatsPanel';
import { TalentSummary } from './components/TalentSummary';
import { CharacterPicker, flatPickerOrder } from './components/CharacterPicker';
import { AddCharacterView } from './components/AddCharacterView';
import { DungeonHelper } from './components/DungeonHelper';
import { CompareView } from './components/CompareView';
import { Dashboard } from './views/Dashboard';
import { AccountView } from './views/AccountView';
import { refreshWowheadTooltips } from './lib/wow';

const POLL_MS = 30_000;
const ADD_HASH = '#add';
const COMPARE_HASH_PREFIX = '#compare/';
const OWNER_HASH_PREFIX = '#owner/';
const CHARACTER_HASH_PREFIX = '#character/';
const DASHBOARD_HASH = '#dashboard';

type View = 'dashboard' | 'account' | 'character' | 'add' | 'compare';

interface Route {
  view: View;
  characterKey: string | null;
  ownerKey: string | null;
  compareKeys: [string, string] | null;
}

/** Parse the current location.hash into a Route. */
function parseHash(): Route {
  const raw = window.location.hash;
  if (!raw || raw === '#' || raw === DASHBOARD_HASH) {
    return { view: 'dashboard', characterKey: null, ownerKey: null, compareKeys: null };
  }
  if (raw === ADD_HASH) {
    return { view: 'add', characterKey: null, ownerKey: null, compareKeys: null };
  }
  if (raw.startsWith(COMPARE_HASH_PREFIX)) {
    const rest = raw.slice(COMPARE_HASH_PREFIX.length);
    const parts = rest.split('/').map(decodeURIComponent);
    if (parts.length === 2 && parts[0] && parts[1]) {
      return { view: 'compare', characterKey: null, ownerKey: null, compareKeys: [parts[0], parts[1]] };
    }
    return { view: 'dashboard', characterKey: null, ownerKey: null, compareKeys: null };
  }
  if (raw.startsWith(OWNER_HASH_PREFIX)) {
    const ownerKey = decodeURIComponent(raw.slice(OWNER_HASH_PREFIX.length));
    return { view: 'account', characterKey: null, ownerKey, compareKeys: null };
  }
  if (raw.startsWith(CHARACTER_HASH_PREFIX)) {
    return {
      view: 'character',
      characterKey: decodeURIComponent(raw.slice(CHARACTER_HASH_PREFIX.length)),
      ownerKey: null,
      compareKeys: null,
    };
  }
  // Legacy: bare "#Name-Realm" hash → character view (back-compat with old bookmarks).
  return {
    view: 'character',
    characterKey: decodeURIComponent(raw.replace(/^#/, '')),
    ownerKey: null,
    compareKeys: null,
  };
}

function setHash(next: string) {
  const final = next.startsWith('#') ? next : `#${next}`;
  if (window.location.hash !== final) {
    window.history.replaceState(null, '', window.location.pathname + window.location.search + final);
  }
}

export default function App() {
  const [route, setRoute] = useState<Route>(parseHash());
  const [chars, setChars] = useState<CharacterSummary[]>([]);
  const [data, setData] = useState<CharacterResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Compare-mode toggle (separate from the routing compare-view).
  const [compareMode, setCompareMode] = useState(false);
  const [compareKeys, setCompareKeys] = useState<string[]>(route.compareKeys ?? []);

  const searchRef = useRef<HTMLInputElement>(null);

  // ----- data loaders --------------------------------------------------

  const loadList = useCallback(async () => {
    try {
      const list = await fetchCharacters();
      setChars(list);
      return list;
    } catch (e: any) {
      setError(e.message || 'Failed to load character list');
      return [] as CharacterSummary[];
    }
  }, []);

  const loadChar = useCallback(async (key: string) => {
    try {
      const c = await fetchCharacterByKey(key);
      setData(c);
      setError(null);
    } catch (e: any) {
      setError(e.message || `Failed to load ${key}`);
    }
  }, []);

  // ----- initial load + polling --------------------------------------

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const list = await loadList();
      if (cancelled) return;
      if (route.view === 'character' && route.characterKey) {
        await loadChar(route.characterKey);
      }
      setLoading(false);
    })();

    const id = setInterval(async () => {
      const list = await loadList();
      // While a character page is open, also refresh it.
      const r = parseHash();
      if (r.view === 'character' && r.characterKey && list.some((c) => c.key === r.characterKey)) {
        await loadChar(r.characterKey);
      }
    }, POLL_MS);

    const onHashChange = () => {
      const r = parseHash();
      setRoute(r);
      if (r.view === 'character' && r.characterKey) {
        setLoading(true);
        loadChar(r.characterKey).finally(() => setLoading(false));
      }
      if (r.view === 'compare' && r.compareKeys) {
        setCompareKeys(r.compareKeys);
      }
    };
    window.addEventListener('hashchange', onHashChange);

    return () => {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener('hashchange', onHashChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { refreshWowheadTooltips(); }, [data]);

  // ----- keyboard shortcuts ------------------------------------------

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const tgt = e.target as HTMLElement | null;
      if (tgt && (tgt.tagName === 'INPUT' || tgt.tagName === 'TEXTAREA' || tgt.isContentEditable)) return;
      if (e.key === '[' || e.key === ']') {
        if (chars.length === 0 || route.view !== 'character') return;
        const ordered = flatPickerOrder(chars);
        const idx = Math.max(0, ordered.findIndex((c) => c.key === route.characterKey));
        const next = e.key === ']'
          ? ordered[(idx + 1) % ordered.length]
          : ordered[(idx - 1 + ordered.length) % ordered.length];
        if (next && next.key !== route.characterKey) openCharacter(next.key);
        e.preventDefault();
      } else if (e.key === '/') {
        searchRef.current?.focus();
        e.preventDefault();
      } else if (e.key.toLowerCase() === 'c') {
        setCompareMode((v) => !v);
      } else if (e.key === 'Escape' && route.view !== 'dashboard') {
        openDashboard();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chars, route]);

  // ----- navigation helpers -----------------------------------------

  function openDashboard() { setHash(DASHBOARD_HASH); setRoute(parseHash()); }
  function openAccount(ownerKey: string) {
    setHash(OWNER_HASH_PREFIX + encodeURIComponent(ownerKey));
    setRoute(parseHash());
  }
  function openCharacter(key: string) {
    setHash(CHARACTER_HASH_PREFIX + encodeURIComponent(key));
    setLoading(true);
    setRoute(parseHash());
    loadChar(key).finally(() => setLoading(false));
  }
  function openAdd() { setHash(ADD_HASH); setRoute(parseHash()); }

  function handleToggleCompareMode() {
    setCompareMode((v) => !v);
    if (compareMode) setCompareKeys([]);
  }
  function handleToggleCompareKey(key: string) {
    setCompareKeys((curr) => {
      if (curr.includes(key)) return curr.filter((k) => k !== key);
      if (curr.length >= 2) return curr;
      const next = [...curr, key];
      if (next.length === 2) {
        const [a, b] = next;
        setHash(`${COMPARE_HASH_PREFIX}${encodeURIComponent(a)}/${encodeURIComponent(b)}`);
        setRoute(parseHash());
      }
      return next;
    });
  }
  function handleExitCompare() {
    setCompareMode(false);
    setCompareKeys([]);
    openDashboard();
  }

  // ----- shared switcher (top-right of every non-modal view) --------

  const switcher = (
    <CharacterPicker
      chars={chars}
      selectedKey={route.view === 'character' ? route.characterKey : null}
      onSelect={openCharacter}
      onAddNew={openAdd}
      onOpenDashboard={openDashboard}
      compareMode={compareMode}
      compareKeys={compareKeys}
      onToggleCompareMode={handleToggleCompareMode}
      onToggleCompareKey={handleToggleCompareKey}
      searchRef={searchRef}
    />
  );

  // ----- top bar (only on dashboard / account, where there's no header) --

  function TopBar() {
    return (
      <header className="top-bar">
        <a
          className="top-bar-brand"
          href="#dashboard"
          onClick={(e) => { e.preventDefault(); openDashboard(); }}
        >
          <span className="top-bar-brand-glow">⚔</span>
          <span className="top-bar-brand-text">armory.thepoodge.com</span>
        </a>
        <div className="top-bar-right">{switcher}</div>
      </header>
    );
  }

  // ----- modal-ish views first ---------------------------------------

  if (route.view === 'add') {
    return <div className="app"><AddCharacterView onBack={openDashboard} /></div>;
  }
  if (route.view === 'compare' && compareKeys.length === 2) {
    return (
      <div className="app">
        <CompareView keyA={compareKeys[0]} keyB={compareKeys[1]} onExit={handleExitCompare} />
      </div>
    );
  }

  if (error && !data && chars.length === 0) {
    return <div className="app error">Error: {error}</div>;
  }

  if (loading && route.view === 'character' && !data) {
    return (
      <div className="app">
        <TopBar />
        <div className="loading-inline">Loading {route.characterKey ?? 'character'}…</div>
      </div>
    );
  }

  // ----- dashboard ----------------------------------------------------

  if (route.view === 'dashboard') {
    return (
      <div className="app">
        <TopBar />
        <Dashboard
          chars={chars}
          onOpenAccount={openAccount}
          onOpenCharacter={openCharacter}
          onAddNew={openAdd}
        />
        <footer>
          <span>armory.thepoodge.com</span>
          <span className="footer-shortcut-hint" title="Keyboard shortcuts">
            / search · c compare · esc home
          </span>
        </footer>
      </div>
    );
  }

  // ----- account view -------------------------------------------------

  if (route.view === 'account' && route.ownerKey !== null) {
    const ownerKey = route.ownerKey;
    const filtered = chars.filter((c) => (c.owner ?? '') === ownerKey);
    const ownerLabel = ownerKey === '' ? 'Legacy / shared' : ownerKey;
    return (
      <div className="app">
        <TopBar />
        <AccountView
          ownerKey={ownerKey}
          ownerLabel={ownerLabel}
          chars={filtered}
          onOpenCharacter={openCharacter}
          onBack={openDashboard}
        />
        <footer>
          <span>armory.thepoodge.com</span>
          <span className="footer-shortcut-hint" title="Keyboard shortcuts">
            / search · c compare · esc home
          </span>
        </footer>
      </div>
    );
  }

  // ----- character view -----------------------------------------------

  if (route.view === 'character' && data) {
    return (
      <div className="app">
        <Header snapshot={data.snapshot} updatedAt={data.updatedAt} right={switcher} />
        <main>
          <section className="paperdoll-wrap">
            <PaperDoll snapshot={data.snapshot} />
          </section>
          <aside>
            <StatsPanel snapshot={data.snapshot} />
            <TalentSummary snapshot={data.snapshot} />
          </aside>
        </main>
        <DungeonHelper anchor={data} candidates={chars} />
        <footer>
          <span>armory.thepoodge.com · HeroExtract v{data.snapshot.addonVersion}</span>
          <span className="footer-shortcut-hint" title="Keyboard shortcuts">
            [ ] cycle · / search · c compare · esc home
          </span>
        </footer>
      </div>
    );
  }

  // ----- fallback: a character was requested by key that we can't find ----

  if (route.view === 'character' && !data && !loading) {
    return (
      <div className="app">
        <TopBar />
        <div className="loading-inline error">
          Couldn't load <code>{route.characterKey}</code>. <a href="#dashboard" onClick={(e) => { e.preventDefault(); openDashboard(); }}>Back to dashboard</a>.
        </div>
      </div>
    );
  }

  return <div className="app loading">Loading…</div>;
}
